// CandidateFormContext.js
import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createCandidate, updateCandidate, updateEmergencyContact, getCandidateById, getEmergencyContact } from "../../api/candidiate";
import { setBasicInfo, setProfessionalInfo, setProfileInfo, setAuditInfo, setEmergencyContacts } from "../../features/candidate/personalSlice";
import { store } from "../../store/store";
import { toast } from 'react-toastify';

const CandidateFormContext = createContext();

// Flatten nested object into a single-level object
const flattenObject = (obj) => {
  let result = {};
  for (const key in obj) {
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      Object.assign(result, flattenObject(obj[key]));
    } else {
      result[key] = obj[key];
    }
  }
  return result;
};

// Define tab order for validation tracking
export const PERSONAL_TABS = ['basic', 'professional', 'profile', 'emergency-contacts'];
export const MARKETING_TABS = ['marketing-history', 'marketing-list', 'notes', 'jobs'];
export const MAIN_SECTIONS = ['personal', 'notes', 'marketing', 'interviews', 'projects', 'trainings', 'documents'];
export const ALL_TABS = [...PERSONAL_TABS, ...MARKETING_TABS];

// Convert MM/DD/YYYY to YYYY-MM-DD for date inputs
const convertDateFormat = (inputDate) => {
  if (!inputDate || typeof inputDate !== 'string') return inputDate;
  const parts = inputDate.split('/');
  if (parts.length === 3) {
    const [month, day, year] = parts;
    return `${year}-${month}-${day}`;
  }
  return inputDate;
};

export const CandidateFormProvider = ({ children }) => {
  const dispatch = useDispatch();
  // Check Redux state for candidateId to determine if editing
  const candidateId = useSelector(state => state.personal?.candidateId);

  const initialFormData = {
    basic: {},
    professional: {},
    profile: {},
    emergencyContacts: [],
    notes: '',
    marketingHistory: {},
    marketingList: {},
    marketingNotes: '',
    marketingJobs: [],
  };
  const [formData, setFormData] = useState(initialFormData);
  // If candidateId exists in Redux, we're editing (not creating)
  const [isCreating, setIsCreating] = useState(!candidateId);

  // Track completed/validated tabs - if editing, all tabs are accessible
  const [completedTabs, setCompletedTabs] = useState(candidateId ? [...ALL_TABS] : []);
  const [currentTab, setCurrentTab] = useState('basic');

  // Track the last-saved server state for change detection across tab navigation
  const [serverPersonalState, setServerPersonalState] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Watch full personal state for global change detection
  const personalState = useSelector(state => state.personal);

  // Compute whether there are any unsaved changes across all tabs
  const hasUnsavedChanges = useMemo(() => {
    if (isCreating || !serverPersonalState) return false;

    // Compare basic, professional, profile sections
    const sections = ['basic', 'professional', 'profile'];
    for (const section of sections) {
      const current = personalState[section];
      const server = serverPersonalState[section];
      if (current && server) {
        for (const key of Object.keys(current)) {
          if (current[key] !== server[key]) return true;
        }
      }
    }

    // Compare emergency contacts
    for (const contact of ['contactInfo1', 'contactInfo2']) {
      const current = personalState.emergencyContacts?.[contact];
      const server = serverPersonalState.emergencyContacts?.[contact];
      if (current && server) {
        for (const key of Object.keys(current)) {
          if (current[key] !== server[key]) return true;
        }
      }
    }

    return false;
  }, [personalState, serverPersonalState, isCreating]);

  // Update isCreating when candidateId changes (e.g., when navigating from list to edit)
  useEffect(() => {
    if (candidateId) {
      setIsCreating(false);
      setCompletedTabs([...ALL_TABS]);
    }
  }, [candidateId]);

  const updateFormData = (section, data) => {
    setFormData((prev) => ({
      ...prev,
      [section]: data,
    }));
  };

  // Call this before starting a new candidate creation
  const startCreateNewCandidate = () => {
    setFormData(initialFormData);
    setIsCreating(true);
    setCompletedTabs([]); // Reset completed tabs
    setCurrentTab('basic'); // Reset to first tab
    setServerPersonalState(null); // Clear server state
  };

  // Mark a tab as completed (validated)
  const markTabComplete = (tabLink) => {
    setCompletedTabs((prev) => {
      if (!prev.includes(tabLink)) {
        return [...prev, tabLink];
      }
      return prev;
    });
  };

  // Mark a tab as incomplete (has errors)
  const markTabIncomplete = (tabLink) => {
    setCompletedTabs((prev) => prev.filter((t) => t !== tabLink));
  };

  // Check if a tab can be accessed (for Personal and Marketing sub-tabs)
  const canAccessTab = (tabLink) => {
    // In update mode, all tabs are accessible
    if (!isCreating) return true;

    // First tab of Personal section is always accessible
    const personalIndex = PERSONAL_TABS.indexOf(tabLink);
    if (personalIndex === 0) return true;

    // For Personal tabs, check if all previous Personal tabs are completed
    if (personalIndex > 0) {
      const previousTabs = PERSONAL_TABS.slice(0, personalIndex);
      return previousTabs.every((tab) => completedTabs.includes(tab));
    }

    // For Marketing tabs, first check if Personal section is complete
    const marketingIndex = MARKETING_TABS.indexOf(tabLink);
    if (marketingIndex >= 0) {
      // Personal must be complete to access Marketing tabs
      if (!isPersonalComplete()) return false;

      // First Marketing tab is accessible if Personal is complete
      if (marketingIndex === 0) return true;

      // Check if all previous Marketing tabs are completed
      const previousMarketingTabs = MARKETING_TABS.slice(0, marketingIndex);
      return previousMarketingTabs.every((tab) => completedTabs.includes(tab));
    }

    return false;
  };

  // Get the next tab link within Personal section
  const getNextPersonalTab = (currentTabLink) => {
    const currentIndex = PERSONAL_TABS.indexOf(currentTabLink);
    if (currentIndex < PERSONAL_TABS.length - 1) {
      return PERSONAL_TABS[currentIndex + 1];
    }
    return null;
  };

  // Get the previous tab link within Personal section
  const getPrevPersonalTab = (currentTabLink) => {
    const currentIndex = PERSONAL_TABS.indexOf(currentTabLink);
    if (currentIndex > 0) {
      return PERSONAL_TABS[currentIndex - 1];
    }
    return null;
  };

  // Get the next tab link within Marketing section
  const getNextMarketingTab = (currentTabLink) => {
    const currentIndex = MARKETING_TABS.indexOf(currentTabLink);
    if (currentIndex < MARKETING_TABS.length - 1) {
      return MARKETING_TABS[currentIndex + 1];
    }
    return null;
  };

  // Get the previous tab link within Marketing section
  const getPrevMarketingTab = (currentTabLink) => {
    const currentIndex = MARKETING_TABS.indexOf(currentTabLink);
    if (currentIndex > 0) {
      return MARKETING_TABS[currentIndex - 1];
    }
    return null;
  };

  // Check if Personal section is complete (all personal tabs validated)
  const isPersonalComplete = () => {
    if (!isCreating) return true; // In update mode, always accessible
    return PERSONAL_TABS.every((tab) => completedTabs.includes(tab));
  };

  // Check if Marketing section is complete (all marketing tabs validated)
  const isMarketingComplete = () => {
    if (!isCreating) return true; // In update mode, always accessible
    return MARKETING_TABS.every((tab) => completedTabs.includes(tab));
  };

  // Check if a main section can be accessed
  const canAccessSection = (sectionLink) => {
    // In update mode, all sections are accessible
    if (!isCreating) return true;

    const sectionIndex = MAIN_SECTIONS.indexOf(sectionLink);

    switch (sectionLink) {
      case 'personal':
        return true; // Personal is always accessible
      case 'notes':
        return isPersonalComplete(); // Notes requires Personal to be complete
      case 'marketing':
        return isPersonalComplete(); // Marketing requires Personal to be complete
      case 'interviews':
        return isPersonalComplete(); // Interviews requires Personal to be complete
      case 'projects':
        return isPersonalComplete(); // Projects requires Personal to be complete
      case 'trainings':
        return isPersonalComplete(); // Trainings requires Personal to be complete
      case 'documents':
        return isPersonalComplete(); // Documents requires Personal to be complete
      default:
        return false;
    }
  };

  const saveCandidateData = async (section, sectionData) => {
    // Get candidateId from Redux
    let candidateId = null;
    try {
      const { store } = await import("../../store/store.js");
      const personalState = store.getState().personal;
      if (personalState && personalState.candidateId) {
        candidateId = personalState.candidateId;
      }
    } catch (e) {
      // fallback: do nothing
    }

    // Build payload based on section
    let payload = sectionData;

    updateFormData(section, sectionData); // update context state immediately

    try {
      let res;
      if (!isCreating && candidateId) {
        // PUT update
        res = await updateCandidate(payload, candidateId);
        toast.success('Candidate updated successfully');
      } else {
        // POST create
        res = await createCandidate(payload);
        toast.success('Candidate created successfully');
        setIsCreating(false); // After creation, switch to update mode
      }
      return res;
    } catch (error) {
      console.error('Error saving candidate:', error);
      toast.error('Failed to save candidate. Please try again.');
      throw error;
    }
  };

  const resetFormData = () => {
    setFormData(initialFormData);
    setIsCreating(true);
    setServerPersonalState(null);
  };

  const resetCandidateData = (newData) => {
    if (newData) {
      setFormData(newData);
    }
    setIsCreating(false);
    // In update mode, mark all tabs as accessible/completed
    setCompletedTabs([...ALL_TABS]);
    // Capture current Redux personal state as the server-saved state
    try {
      const personalState = store.getState().personal;
      setServerPersonalState(JSON.parse(JSON.stringify(personalState)));
    } catch (e) {
      // fallback
    }
  };

  // Update candidate data via API (called from Update buttons on each tab)
  const updateCandidateData = async () => {
    const personalState = store.getState().personal;
    const cId = personalState?.candidateId;
    if (!cId) {
      toast.error('No candidate to update');
      return;
    }

    // Flatten the personal state (excluding emergency contacts)
    const parseBody = flattenObject({ ...personalState, emergencyContacts: {} });

    try {
      const res = await updateCandidate(parseBody, cId);
      // Update server state snapshot after successful save
      setServerPersonalState(JSON.parse(JSON.stringify(personalState)));
      toast.success('Candidate updated successfully');
      return res;
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 400) {
        const detail = data?.details || data?.message || 'Invalid request payload';
        toast.error(`Validation Error: ${detail}`);
      } else if (status === 409) {
        const conflicting = data?.conflictingFields;
        if (conflicting) {
          const fields = Object.keys(conflicting).join(', ');
          toast.error(`Conflict: A candidate with this ${fields} already exists.`);
        } else {
          toast.error(data?.message || 'A candidate with these details already exists.');
        }
      } else if (status === 401) {
        toast.error('Unauthorized: Please log in again.');
      } else {
        toast.error(data?.message || 'Failed to update candidate. Please try again.');
      }
      throw err;
    }
  };

  // Helper: check if a single contact has changed compared to server state
  const isContactChanged = (contactKey) => {
    const current = store.getState().personal?.emergencyContacts?.[contactKey];
    const server = serverPersonalState?.emergencyContacts?.[contactKey];
    if (!current || !server) return false;
    const editableFields = ['firstName', 'lastName', 'contactPhoneNum', 'email', 'contactRelation'];
    return editableFields.some(key => current[key] !== server[key]);
  };

  // Update emergency contacts via API
  const updateEmergencyContactData = async () => {
    const personalState = store.getState().personal;
    const cId = personalState?.candidateId;
    if (!cId) {
      toast.error('No candidate to update');
      return;
    }

    try {
      const { contactInfo1, contactInfo2 } = personalState.emergencyContacts;
      const contact1Changed = isContactChanged('contactInfo1');
      const contact2Changed = isContactChanged('contactInfo2');

      if (!contact1Changed && !contact2Changed) {
        toast.info('No changes to save');
        return;
      }

      if (contact1Changed) {
        await updateEmergencyContact(contactInfo1, cId, 1);
        toast.success('Emergency contact 1 updated successfully');
      }
      if (contact2Changed) {
        await updateEmergencyContact(contactInfo2, cId, 2);
        toast.success('Emergency contact 2 updated successfully');
      }

      // Update server state snapshot after successful save
      setServerPersonalState(JSON.parse(JSON.stringify(personalState)));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update emergency contacts.');
      throw err;
    }
  };

  // Re-fetch candidate data from API and update Redux state
  const refreshCandidateData = async (cId) => {
    try {
      const res = await getCandidateById(cId);
      const c = res.data?.response?.data?.[0] || res.data?.data || {};
      if (!c.candidate_id) return;

      dispatch(setBasicInfo({
        firstName: c.first_name, lastName: c.last_name, middleName: c.middle_name,
        gender: c.gender ? c.gender.toLowerCase() : c.gender,
        maritalStatus: c.marital_status ? c.marital_status.toLowerCase() : c.marital_status,
        dob: convertDateFormat(c.date_of_birth || c.dob),
        email: c.email_id, address1: c.address1, address2: c.address2,
        phone: c.mobile, alternatePhoneNum: c.phone,
        city: c.city || '', state: c.state || '', zip: c.zip || '', status: c.status || '',
      }));
      dispatch(setProfessionalInfo({
        jobTitle: c.job_title, salesPerson: c.sales_person_id,
        technology: c.talent_id ? String(c.talent_id) : '',
        workPermitStatus: c.legal_status || '',
        workStatusIssueDate: convertDateFormat(c.work_status_issue_date),
        workStatusEndDate: convertDateFormat(c.work_status_enddate),
        SSN: c.ssn, employmentStatus: c.employment_status || '', referredBy: c.referred_by,
      }));
      dispatch(setProfileInfo({
        totalInterviews: c.total_interviews, activeInterviews: c.total_active_interviews,
        passedInterviews: c.total_passed_interviews, failedInterviews: c.total_failed_interviews,
        holdInterviews: c.total_onhold_interviews,
        lastInterviewOn: convertDateFormat(c.last_interview_on), rating: c.rating,
      }));
      dispatch(setAuditInfo({
        createdBy: c.created_by, dateCreated: c.created,
        updatedBy: c.updated_by, lastUpdated: c.updated,
      }));

      // Refresh emergency contacts
      try {
        const ecRes = await getEmergencyContact(cId);
        const ecData = ecRes.data?.data || {};
        const getContact = (ct) => {
          if (!ct) return {};
          if (Array.isArray(ct)) return ct[0] || {};
          return ct;
        };
        const extractPhone = (pf) => {
          if (!pf) return '';
          if (typeof pf === 'object') return pf.phone || '';
          return String(pf);
        };
        const mapContact = (ct) => ({
          firstName: ct.first_name || '', lastName: ct.last_name || '',
          contactPhoneNum: extractPhone(ct.phone), email: ct.email || '',
          contactRelation: ct.relation || '',
          createdBy: ct.created_by || '', dateCreated: ct.created || '',
          updatedBy: ct.updated_by || '', lastUpdated: ct.updated || '',
        });
        dispatch(setEmergencyContacts({
          contact1: mapContact(getContact(ecData.contact_id1)),
          contact2: mapContact(getContact(ecData.contact_id2)),
        }));
      } catch (ecErr) {
        // Don't block - emergency contacts may not exist
      }

      // Update server state snapshot with fresh data
      setServerPersonalState(JSON.parse(JSON.stringify(store.getState().personal)));
    } catch (err) {
      console.error('Error refreshing candidate data:', err);
    }
  };

  // Unified save: saves candidate data + emergency contacts as needed
  const saveAllChanges = async () => {
    const currentState = store.getState().personal;
    const cId = currentState?.candidateId;
    if (!cId) {
      toast.error('No candidate to update');
      return;
    }

    setIsSaving(true);
    try {
      // Determine what changed
      const candidateFieldsChanged = ['basic', 'professional', 'profile'].some(section => {
        const current = currentState[section];
        const server = serverPersonalState?.[section];
        if (!current || !server) return false;
        return Object.keys(current).some(key => current[key] !== server[key]);
      });

      const contact1Changed = isContactChanged('contactInfo1');
      const contact2Changed = isContactChanged('contactInfo2');

      // Save candidate data first if changed
      if (candidateFieldsChanged) {
        const parseBody = flattenObject({ ...currentState, emergencyContacts: {}, audit: {} });
        await updateCandidate(parseBody, cId);
        toast.success('Candidate updated successfully');
      }

      // Save only the changed contact(s)
      if (contact1Changed) {
        await updateEmergencyContact(currentState.emergencyContacts.contactInfo1, cId, 1);
        toast.success('Emergency contact 1 updated successfully');
      }
      if (contact2Changed) {
        await updateEmergencyContact(currentState.emergencyContacts.contactInfo2, cId, 2);
        toast.success('Emergency contact 2 updated successfully');
      }

      // Re-fetch from backend to get updated audit info and latest data
      await refreshCandidateData(cId);
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 400) {
        const detail = data?.details || data?.message || 'Invalid request payload';
        toast.error(`Validation Error: ${detail}`);
      } else if (status === 409) {
        const conflicting = data?.conflictingFields;
        if (conflicting) {
          const fields = Object.keys(conflicting).join(', ');
          toast.error(`Conflict: A candidate with this ${fields} already exists.`);
        } else {
          toast.error(data?.message || 'A candidate with these details already exists.');
        }
      } else if (status === 401) {
        toast.error('Unauthorized: Please log in again.');
      } else {
        toast.error(data?.message || 'Failed to update candidate. Please try again.');
      }
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CandidateFormContext.Provider value={{
      formData,
      updateFormData,
      saveCandidateData,
      resetCandidateData,
      resetFormData,
      isCreating,
      startCreateNewCandidate,
      // Tab validation functions
      completedTabs,
      currentTab,
      setCurrentTab,
      markTabComplete,
      markTabIncomplete,
      canAccessTab,
      getNextPersonalTab,
      getPrevPersonalTab,
      getNextMarketingTab,
      getPrevMarketingTab,
      // Section validation functions
      isPersonalComplete,
      isMarketingComplete,
      canAccessSection,
      // Update API functions
      updateCandidateData,
      updateEmergencyContactData,
      serverPersonalState,
      // Global save
      hasUnsavedChanges,
      isSaving,
      saveAllChanges
    }}>
      {children}
    </CandidateFormContext.Provider>
  );
};

export const useCandidateForm = () => useContext(CandidateFormContext);
